/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import BaseCom from "../BaseCom";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import L from "../Lang";
import Card from "../Card";
import FormInput from "../FormInput";
import AppActions from "../AppActions";
import ConfirmButton from "../ConfirmButton";
import Auth from "../../shared/Auth";
import { qreq } from "../../shared/qrequest";
import Address from "../Address";

class MyInfo extends BaseCom {
    state = {
        info: {
            firstName: "",
            lastName: "",

            enableSMSNotifications: false
        },
        address: {},
        loading: true,
        saving: false,
        hasChanges: false
    };

    async componentDidMount() {
        await this.loadInfo();
        await this.loadAddress();
    }

    loadInfo = async () => {
        this.setState({ loading: true });

        try {
            const user = await Auth.getLoginAsync();

            if (!user) {
                this.alert("Unable to load your account information.");
                return;
            }

            this.setState({
                info: { ...user },
                hasChanges: false
            });
        }
        catch (error) {
            this.unkownErrorCallback(error);
        }
        finally {
            this.setState({ loading: false });
        }
    };

    loadAddress = async () => {
        try {
            const j = await qreq.getAsync('/api/my/address');
            if (j.item) {
                this.setState({ address: j.item });
            }
            else
                this.alert(j.errorMessage);
        }
        catch (e) {
            this.unkownErrorCallback(e);
        }
    }

    submit = async (event) => {
        event?.preventDefault();

        if (this.state.saving || !this.state.hasChanges)
            return;

        this.setState({ saving: true });

        try {
            const response = await qreq.postAsync(
                "/api/my/info/save",
                { ...this.state.info, address: this.state.address }
            );

            if (response?.errorCode !== 0) {
                this.alert(
                    response?.errorMessage ||
                    "Unable to save your information."
                );
                return;
            }

            this.setState({ hasChanges: false });
            this.success("Your information was saved.");
        }
        catch (error) {
            this.unkownErrorCallback(error);
        }
        finally {
            this.setState({ saving: false });
        }
    };

    change = () => {
        this.setState({
            info: { ...this.state.info, address: this.state.address },
            hasChanges: true
        });
    };

    renderLoading() {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div
                    className="spinner-border"
                    role="status"
                    aria-label="Loading"
                >
                    <span className="visually-hidden">
                        <L>Loading</L>
                    </span>
                </div>
            </div>
        );
    }

    render() {
        const {
            info,
            loading,
            saving,
            hasChanges
        } = this.state;

        return (
            <div className="container-fluid">
                <h1><L>My Information</L></h1>

                {loading
                    ? this.renderLoading()
                    : (
                        <form onSubmit={this.submit}>
                            <AppActions>
                                <ConfirmButton
                                    lg
                                    submit
                                    icon="faSave"
                                    disabled={saving || !hasChanges}
                                >
                                    <L>{saving ? "Saving" : "Save"}</L>
                                </ConfirmButton>
                            </AppActions>

                            <div className="row g-4">
                                <div className="col-12 col-xl-8">
                                    <Card title="User Demographics">
                                        <div className="row g-3">
                                            <div className="col-12 col-md-6">
                                                <FormInput
                                                    model={info}
                                                    name="firstName"
                                                    label="First Name"
                                                    autoComplete="given-name"
                                                    required
                                                    onChange={this.change}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <FormInput
                                                    model={info}
                                                    name="lastName"
                                                    label="Last Name"
                                                    autoComplete="family-name"
                                                    required
                                                    onChange={this.change}
                                                />
                                            </div>
                                        </div>
                                    </Card>
                                </div>

                                <div className="col-12 col-xl-4">
                                    <Card title="Notifications">
                                        <div className="d-flex flex-column gap-2">
                                            <FormInput
                                                model={info}
                                                name="enableSMSNotifications"
                                                label="SMS Notifications"
                                                type="switch"
                                                onChange={this.change}
                                            />

                                            <small className="text-body-secondary">
                                                <L>
                                                    Receive important account and service notifications by text message.
                                                </L>
                                            </small>
                                        </div>
                                    </Card>
                                </div>
                                <div className="row g-4">
                                    <div className="col-12 col-xl-12">
                                        <Address item={this.state.address} onChange={this.change} />
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
            </div>
        );
    }
}

export default withParamsAndNavigate(MyInfo);